// ============================================
// VFX Binding Layout Constants
// ============================================
// Single source of truth for all VFX shader bindings.
// All VFX shaders MUST use these binding indices.

import { COUNTERS_LAYOUT, EVENT_COUNT_LAYOUT, GPU_EVENT_LAYOUT } from './compiler/Layouts';

/**
 * Maximum number of GPU events that can be emitted per frame.
 * Events beyond this limit are dropped.
 */
export const VFX_MAX_EVENTS = 1024;

/**
 * GPU event struct size in bytes (derived from GPU_EVENT_LAYOUT).
 */
export const VFX_EVENT_STRUCT_SIZE = GPU_EVENT_LAYOUT.size;

/**
 * Default workgroup size for VFX compute shaders.
 */
export const VFX_DEFAULT_WORKGROUP_SIZE = 64;

/**
 * WebGPU max workgroups per dimension limit.
 * This is a hardware/API limitation that cannot be exceeded.
 */
export const VFX_MAX_WORKGROUPS_PER_DIMENSION = 65535;

/**
 * Maximum particles per system.
 * Computed as: maxWorkgroups * workgroupSize = 65535 * 64 = 4,194,240
 * 
 * Going above this limit will cause WebGPU dispatch errors.
 * For higher particle counts, use multiple systems.
 */
export const VFX_MAX_PARTICLES = VFX_MAX_WORKGROUPS_PER_DIMENSION * VFX_DEFAULT_WORKGROUP_SIZE;

/**
 * VFX compute shader binding indices.
 * 
 * Layout:
 * - binding 0: Uniforms (time, deltaTime, maxParticles, emitter position, etc.)
 * - binding 1: Particles storage buffer (array of Particle structs)
 * - binding 2: Counters storage buffer (per-system atomic counters)
 * - binding 3: Events storage buffer (inter-system communication)
 * - binding 4: VFXVariables uniform (user-defined shader variables)
 * - binding 5: EventCount storage buffer (shared event counter)
 * - binding 6: DeadList storage buffer (free particle indices for recycling)
 */
export const VFX_BINDINGS = {
  /** Uniform buffer: time, deltaTime, maxParticles, emitter position, etc. */
  UNIFORMS: 0,
  /** Storage buffer: particle data array */
  PARTICLES: 1,
  /** Storage buffer: atomic counters (aliveCount, deadCount, emitCount, etc.) */
  COUNTERS: 2,
  /** Storage buffer: GPU events for inter-system triggers */
  EVENTS: 3,
  /** Uniform buffer: user-defined VFX variables */
  VFX_VARIABLES: 4,
  /** Storage buffer: shared event count (single atomic u32) */
  EVENT_COUNT: 5,
  /** Storage buffer: dead list (free particle indices for recycling) */
  DEAD_LIST: 6,
} as const;

/**
 * Number of storage buffers used by VFX shaders.
 * Used for createComputePipeline({ storageBufferCount }).
 * 
 * storageBuffers array mapping:
 * - index 0 → PARTICLES  (binding 1)
 * - index 1 → COUNTERS   (binding 2)
 * - index 2 → EVENTS     (binding 3)
 * - index 3 → EVENT_COUNT (binding 5) - skips VFX_VARIABLES which is uniform
 * - index 4 → DEAD_LIST  (binding 6)
 */
export const VFX_STORAGE_BUFFER_COUNT = 5;

/**
 * Mapping from storage buffer array index to binding constant.
 * Used by getStorageBufferBinding() to avoid magic numbers.
 */
const STORAGE_INDEX_TO_BINDING = [
  VFX_BINDINGS.PARTICLES,    // index 0 → binding 1
  VFX_BINDINGS.COUNTERS,     // index 1 → binding 2
  VFX_BINDINGS.EVENTS,       // index 2 → binding 3
  VFX_BINDINGS.EVENT_COUNT,  // index 3 → binding 5 (skipping VFX_VARIABLES)
  VFX_BINDINGS.DEAD_LIST,    // index 4 → binding 6
] as const;

/**
 * Counter buffer layout (per-system, derived from COUNTERS_LAYOUT).
 * 
 * Fields:
 * - aliveCount: Number of alive particles (for stats/debugging)
 * - deadCount: Stack pointer for dead list (number of free slots available)
 * - emitCount: Number of particles to spawn this frame
 * - _unused: Padding for alignment
 * - spawnAccumulator: Fixed-point accumulator for fractional spawns
 * - spawnOffset: Legacy field (unused with dead list system)
 */
export const VFX_COUNTER_BUFFER_SIZE = COUNTERS_LAYOUT.size;

/**
 * Counter buffer field offsets (in u32 units).
 */
export const VFX_COUNTER_FIELDS = {
  ALIVE_COUNT: 0,
  DEAD_COUNT: 1,
  EMIT_COUNT: 2,
  _UNUSED: 3,
  SPAWN_ACCUMULATOR: 4,
  SPAWN_OFFSET: 5,
} as const;

/**
 * Counter buffer field byte offsets for updateBuffer calls.
 * Derived from COUNTERS_LAYOUT.offsets.
 */
export const VFX_COUNTER_BYTE_OFFSETS = {
  ALIVE_COUNT: COUNTERS_LAYOUT.offsets.aliveCount,
  DEAD_COUNT: COUNTERS_LAYOUT.offsets.deadCount,
  EMIT_COUNT: COUNTERS_LAYOUT.offsets.emitCount,
  _UNUSED: COUNTERS_LAYOUT.offsets._unused,
  SPAWN_ACCUMULATOR: COUNTERS_LAYOUT.offsets.spawnAccumulator,
  SPAWN_OFFSET: COUNTERS_LAYOUT.offsets.spawnOffset,
} as const;

/**
 * Event count buffer size (shared between systems, derived from EVENT_COUNT_LAYOUT).
 */
export const VFX_EVENT_COUNT_BUFFER_SIZE = EVENT_COUNT_LAYOUT.size;

/**
 * Generate WGSL binding declarations for VFX compute shaders.
 * Use this to ensure all shaders use consistent bindings.
 */
export function generateVFXBindingDeclarations(): string {
  return `
// VFX Standard Bindings (see VFXBindings.ts)
@group(0) @binding(${VFX_BINDINGS.UNIFORMS}) var<uniform> uniforms: Uniforms;
@group(0) @binding(${VFX_BINDINGS.PARTICLES}) var<storage, read_write> particles: array<Particle>;
@group(0) @binding(${VFX_BINDINGS.COUNTERS}) var<storage, read_write> counters: Counters;
@group(0) @binding(${VFX_BINDINGS.EVENTS}) var<storage, read_write> events: array<GPUEvent>;
@group(0) @binding(${VFX_BINDINGS.VFX_VARIABLES}) var<uniform> vfxVariables: VFXVariables;
@group(0) @binding(${VFX_BINDINGS.EVENT_COUNT}) var<storage, read_write> eventCount: EventCount;
@group(0) @binding(${VFX_BINDINGS.DEAD_LIST}) var<storage, read_write> deadList: array<u32>;
`;
}

/**
 * Get the binding index for a storage buffer array index.
 * Uses STORAGE_INDEX_TO_BINDING lookup table to avoid magic numbers.
 * 
 * @param storageIndex - Index in the storageBuffers array (0-4)
 * @returns The binding index from VFX_BINDINGS
 */
export function getStorageBufferBinding(storageIndex: number): number {
  if (storageIndex < 0 || storageIndex >= STORAGE_INDEX_TO_BINDING.length) {
    throw new Error(`Invalid storage buffer index: ${storageIndex}. Valid range: 0-${STORAGE_INDEX_TO_BINDING.length - 1}`);
  }
  return STORAGE_INDEX_TO_BINDING[storageIndex];
}
